import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback/useDebouncedCallback';
import { SEARCH_STATE, SearchState } from './Search.constants';

const INITIAL_ACTIVE_SUGGESTION_INDEX = -1;
const MIN_SUGGESTIONS_QUERY_LENGTH = 0;

interface UseSearchControllerParams {
  autoSuggestions: string[];
  debounce: number;
  disabled: boolean;
  onChange?: (value: string) => void;
  onClear?: () => void;
  onSuggestionSelect?: (suggestion: string) => void;
  state?: SearchState;
  value: string;
}

interface UseSearchControllerResult {
  activeSuggestionIndex: number;
  filteredSuggestions: string[];
  handleBlur: () => void;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleClear: () => void;
  handleFocus: () => void;
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  handleSuggestionClick: (suggestion: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  internalValue: string;
  isDisabled: boolean;
  isFilled: boolean;
  isFocusedVisible: boolean;
  isHoveredVisible: boolean;
  isSuggestionsVisible: boolean;
}

export const useSearchController = ({
  autoSuggestions,
  debounce,
  disabled,
  onChange,
  onClear,
  onSuggestionSelect,
  state,
  value,
}: UseSearchControllerParams): UseSearchControllerResult => {
  const [internalValue, setInternalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(INITIAL_ACTIVE_SUGGESTION_INDEX);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInternalValue(currentValue => (currentValue !== value ? value : currentValue));
  }, [value]);

  const { debouncedCallback: debouncedOnChange, cancel } = useDebouncedCallback(
    (newValue: string) => onChange?.(newValue),
    debounce,
  );

  const filteredSuggestions = useMemo(() => {
    const normalizedQuery = internalValue.toLowerCase();

    if (normalizedQuery.length === 0) {
      return autoSuggestions;
    }

    return autoSuggestions.filter(suggestion => suggestion.toLowerCase().includes(normalizedQuery));
  }, [autoSuggestions, internalValue]);

  const isStateDisabled = state === SEARCH_STATE.DISABLED;
  const isHoveredVisible = state === SEARCH_STATE.HOVER || state === SEARCH_STATE.FILLED_HOVER;
  const isStateFocused = state === SEARCH_STATE.FOCUSED || state === SEARCH_STATE.AUTOSUGGESTION;
  const isSuggestionsForced = state === SEARCH_STATE.AUTOSUGGESTION;
  const isDisabled = disabled || isStateDisabled;
  const isFocusedVisible = isFocused || isStateFocused;
  const isFilled = internalValue.length > 0;
  const isSuggestionsVisible =
    (isSuggestionsForced && filteredSuggestions.length > 0 && internalValue.length > MIN_SUGGESTIONS_QUERY_LENGTH) ||
    (isFocused &&
      showSuggestions &&
      internalValue.length > MIN_SUGGESTIONS_QUERY_LENGTH &&
      filteredSuggestions.length > 0);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      setShowSuggestions(true);
      setActiveSuggestionIndex(INITIAL_ACTIVE_SUGGESTION_INDEX);
      debouncedOnChange(newValue);
    },
    [debouncedOnChange],
  );

  const handleClear = useCallback(() => {
    cancel();
    setInternalValue('');
    setShowSuggestions(false);
    setActiveSuggestionIndex(INITIAL_ACTIVE_SUGGESTION_INDEX);
    onChange?.('');
    onClear?.();
    inputRef.current?.focus();
  }, [cancel, onChange, onClear]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (internalValue.length > 0) {
      setShowSuggestions(true);
    }
  }, [internalValue]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    setShowSuggestions(false);
    setActiveSuggestionIndex(INITIAL_ACTIVE_SUGGESTION_INDEX);
  }, []);

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      cancel();
      setInternalValue(suggestion);
      setShowSuggestions(false);
      setActiveSuggestionIndex(INITIAL_ACTIVE_SUGGESTION_INDEX);
      onChange?.(suggestion);
      onSuggestionSelect?.(suggestion);
    },
    [cancel, onChange, onSuggestionSelect],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!isSuggestionsVisible) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveSuggestionIndex(prev => (prev < filteredSuggestions.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveSuggestionIndex(prev => (prev > 0 ? prev - 1 : filteredSuggestions.length - 1));
          break;
        case 'Enter':
          if (activeSuggestionIndex >= 0) {
            e.preventDefault();
            handleSuggestionClick(filteredSuggestions[activeSuggestionIndex]);
          }
          break;
        case 'Escape':
          setShowSuggestions(false);
          setActiveSuggestionIndex(INITIAL_ACTIVE_SUGGESTION_INDEX);
          break;
      }
    },
    [isSuggestionsVisible, activeSuggestionIndex, filteredSuggestions, handleSuggestionClick],
  );

  return {
    activeSuggestionIndex,
    filteredSuggestions,
    handleBlur,
    handleChange,
    handleClear,
    handleFocus,
    handleKeyDown,
    handleSuggestionClick,
    inputRef,
    internalValue,
    isDisabled,
    isFilled,
    isFocusedVisible,
    isHoveredVisible,
    isSuggestionsVisible,
  };
};
